import { useMemo } from 'react';
import { MenuAction, TableSort } from '@/types';
import { handleFinancialDeliveryDateDeal } from '@/utils';
import { Client, HubspotPlan } from '@types';
import './TableClient.css';
import { GenericTable } from '@/components/Table/Table';
import {
  getTableClientsColumns,
  ClientTableColumnData,
  mapClientToClientTableColumns,
} from './table.util';

interface TableClientsProps {
  sortedClientRecords: Client[];
  actions: MenuAction[];
  sort: TableSort<ClientTableColumnData>;
  sortBy: (fieldName: keyof ClientTableColumnData) => void;
  selectedPage: number;
  setSelectedPage: (page: number) => void;
  recordsPerPage: number;
  setRecordsPerPage: (recordsPerPage: number) => void;
  handleFinancialDeliveryDate: (
    financialDate: Date | undefined,
    fileDate: string | undefined
  ) => boolean | undefined;
  formatDate: (date?: string) => string;
  formatString: (string?: string) => string | null;
  countActiveUsers: (client: Client) => number;
  hasSyftUrl: (client: Client) => number;
  hasQbo: (client: Client) => boolean;
  handleController: (plan: HubspotPlan | undefined) => string | undefined;
  showDeliveryDateColumn: boolean | undefined;
}

// eslint-disable-next-line import/prefer-default-export
export function TableClients({
  sortedClientRecords,
  actions,
  sort,
  sortBy,
  selectedPage,
  setSelectedPage,
  recordsPerPage,
  setRecordsPerPage,
  handleFinancialDeliveryDate,
  formatDate,
  formatString,
  countActiveUsers,
  hasSyftUrl,
  hasQbo,
  handleController,
  showDeliveryDateColumn,
}: TableClientsProps) {
  const mappedClientData = useMemo(() => {
    return sortedClientRecords.map((client) =>
      mapClientToClientTableColumns(client, {
        countActiveUsers,
        hasSyftUrl,
        hasQbo,
        handleController,
      })
    );
  }, [
    sortedClientRecords,
    countActiveUsers,
    hasSyftUrl,
    hasQbo,
    handleController,
  ]);

  const columns = useMemo(
    () =>
      getTableClientsColumns({
        actions,
        showDeliveryDateColumn,
        handleFinancialDeliveryDate,
        handleFinancialDeliveryDateDeal,
        formatDate,
        formatString,
        countActiveUsers,
        hasSyftUrl,
        hasQbo,
        handleController,
      }),
    [
      actions,
      showDeliveryDateColumn,
      handleFinancialDeliveryDate,
      formatDate,
      formatString,
      countActiveUsers,
      hasSyftUrl,
      hasQbo,
      handleController,
    ]
  );

  return (
    <GenericTable
      data={mappedClientData}
      tableName='clients'
      columns={columns}
      sortBy={sortBy}
      sort={sort}
      recordsPerPage={recordsPerPage}
      selectedPage={selectedPage}
      onPageSelect={setSelectedPage}
      onSelectRowsChange={setRecordsPerPage}
    />
  );
}
